import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Service, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { setLoading } from '@datorama/akita';
import { NGXLogger } from 'ngx-logger';
import { EMPTY, Observable, throwError } from 'rxjs';
import {
  catchError,
  first,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';

import { AppConfig, Dictionary, UserFriendlyError } from 'models';

import { UserUiSettings, UserUiSettingsSet } from 'src/app/models';
import { BrowserPermissionsService } from 'src/app/services/browser-permissions.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ApplicationQuery } from 'src/app/state/application/application.query';
import { ApplicationService } from 'src/app/state/application/application.service';

import { LocalNetworkAccessBlockedSnackbarComponent } from '../components/local-network-access-blocked-snackbar/local-network-access-blocked-snackbar.component';
import { AppConfigQuery } from './app-config.query';
import { AppConfigStore, defaultAppConfig } from './app-config.store';

/** Cached app config request. */
type CachedAppConfig = {
  observable: Observable<AppConfig>;
  timestamp: number;
};

/** Cache for app config requests. */
type AppConfigCache = Map<string, CachedAppConfig>;

/** Application Config Service. */
@Service()
export class AppConfigService {
  private requestCache: AppConfigCache = new Map();
  private static REQUEST_CACHE_DURATION_MS = 30000;

  private appConfigStore = inject(AppConfigStore);
  private appConfigQuery = inject(AppConfigQuery);
  private applicationService = inject(ApplicationService);
  private applicationQuery = inject(ApplicationQuery);
  private notificationService = inject(NotificationService);
  private browserPermissionsService = inject(BrowserPermissionsService);
  private snackBar = inject(MatSnackBar);
  private http = inject(HttpClient);
  private logger = inject(NGXLogger);

  constructor() {
    // When the user settings defaults change, update the application settings where not set by the user.
    const appliedUserUiSettingsDefaults$ =
      this.appConfigQuery.userUiSettingsDefaults$.pipe(
        map((overrides) => {
          if (Object.keys(overrides).length > 0) {
            // Remove any settings that have been set by the user from the overrides.
            const userSet = this.applicationQuery.userUiSettingsSetByUser;
            for (const key of Object.keys(userSet)) {
              if (userSet[key as keyof UserUiSettingsSet]) {
                delete overrides[key as keyof UserUiSettings];
              }
            }
            // Apply the remaining overrides.
            this.logger.debug(
              'Applying user UI settings defaults from app config:',
              overrides
            );
            this.applicationService.applyUserUiSettings(overrides, false);
          }
        })
      );
    appliedUserUiSettingsDefaults$.subscribe();
    // After applying the defaults, apply any forced settings.
    appliedUserUiSettingsDefaults$
      .pipe(
        first(), // Wait for the defaults to be applied.
        switchMap(() => this.appConfigQuery.userUiSettingsForced$)
      )
      .subscribe((overrides) => {
        // Similar to defaults, but do not remove any settings that have been set by the user.
        // The settings will still need to be disabled elsewhere if applied here or the user
        // will be able to change them.
        this.logger.debug(
          'Applying user UI settings forced from app config:',
          overrides
        );
        this.applicationService.applyUserUiSettings(overrides, false);
      });
  }

  /**
   * Gets the app config from the instance.
   *
   * @param instanceUrl Instance URL.
   * @returns An observable app config.
   */
  get(instanceUrl: string): Observable<AppConfig> {
    // If the instance URL is empty, return empty observable.
    if (!instanceUrl) {
      this.logger.debug('No instance URL provided.');
      return EMPTY;
    }

    const now = Date.now();

    // Clean up all expired entries from the cache
    for (const [key, value] of this.requestCache.entries()) {
      if (now - value.timestamp >= AppConfigService.REQUEST_CACHE_DURATION_MS) {
        this.requestCache.delete(key);
      }
    }

    // Check if the request is already cached and within the expiration window
    const cached = this.requestCache.get(instanceUrl);
    if (
      cached &&
      now - cached.timestamp < AppConfigService.REQUEST_CACHE_DURATION_MS
    ) {
      this.logger.debug(`Replaying cached request for ${instanceUrl}`);
      return cached.observable.pipe(
        tap((appConfig) =>
          this.logger.debug(
            `Cached request for ${instanceUrl} replayed`,
            appConfig
          )
        )
      );
    }

    // Create a new request and cache it
    const request$ = this.http
      .get<AppConfig>(`${instanceUrl}/public/config.json`, {
        headers: new HttpHeaders({
          // Request the file without cache.
          'Cache-Control': 'no-cache',
          // biome-ignore lint/style/useNamingConvention: HTTP Header
          Pragma: 'no-cache',
        }),
      })
      .pipe(
        // If there was an error (e.g. file not found, network error), log it and return an empty observable.
        catchError((error) => {
          this.logger.error(
            `Failed to load configuration from instance at ${instanceUrl}.`,
            error
          );
          // Attempt to determine if the request was blocked by lack of local network access permissions.
          return this.browserPermissionsService
            .checkLocalNetworkAccessBlocked()
            .pipe(
              switchMap((isBlocked) => {
                let err: UserFriendlyError;
                if (isBlocked) {
                  err = new UserFriendlyError(
                    'Local network access is blocked',
                    'The application was unable to load the configuration from the instance because the browser is blocking local network access. Please check your browser settings to allow local network access for this site.',
                    'LOCAL_NETWORK_ACCESS_BLOCKED'
                  );
                  // Log the error to the user.
                  this.notificationService.error(err);
                  // Show a snackbar notification so we can include buttons for "More info" and "Dismiss"
                  this.snackBar.openFromComponent(
                    LocalNetworkAccessBlockedSnackbarComponent
                  );
                  return EMPTY; // Return empty because this url will not work no matter what format the url is in.
                } else {
                  err = new UserFriendlyError(
                    'Failed to load configuration',
                    `The application was unable to load the configuration from the instance at ${instanceUrl}. Please check the instance URL and ensure the instance is running.`,
                    'CONFIG_LOAD_FAILED'
                  );
                  // Return an error so that the pipeline doesn't just stop and is allowed to continue in the event that other attempts at retrieving the config are made.
                  return throwError(() => err);
                }
              })
            );
        }),
        setLoading(this.appConfigStore),
        map((appConfig) => {
          this.logger.debug('Loading configuration from instance.');
          /** Keys not present in the instance configuration that will be defaulted. */
          const defaultedKeys = Object.keys(defaultAppConfig).filter(
            (key) => !Object.hasOwn(appConfig, key)
          );

          /** Keys present in the instance configuration that are not part of our expected object. */
          const uknownKeys = Object.keys(appConfig).filter(
            (key) => !Object.hasOwn(defaultAppConfig, key)
          );

          // Warn for keys that will use default values.
          for (const defaultedKey of defaultedKeys) {
            this.logger.info(
              `Application configuration from instance did not contain a key for "${defaultedKey}", value will default to:`,
              JSON.stringify(
                // biome-ignore lint/suspicious/noExplicitAny: Specifically for accessing keys that may not be known to be on the object.
                (defaultAppConfig as Dictionary<any>)[defaultedKey]
              )
            );
          }

          // Warn for keys that were not expected.
          for (const uknownKey of uknownKeys) {
            this.logger.info(
              `Instance configuration contained an unknown key "${uknownKey}". Value will be ignored.`
            );
          }

          // Merge instance configuration against default configuration, then store it.
          const mergedConfig = { ...defaultAppConfig, ...appConfig };
          this.update(mergedConfig);

          // Check if forceUiHost is configured and redirect if necessary
          this.checkAndRedirectForceUiHost(mergedConfig.forceUiHost);

          return mergedConfig;
        }),
        shareReplay(1)
      );

    // Cache the request and timestamp
    this.requestCache.set(instanceUrl, {
      observable: request$,
      timestamp: now,
    });
    return request$;
  }

  /**
   * Reset the app config back to its default state.
   *
   * Also clears the request cache. Cached requests replay their result without
   * re-running the store update, so a cached config would not be reapplied after a reset.
   */
  resetToDefault(): void {
    this.requestCache.clear();
    this.appConfigStore.reset();
  }

  /**
   * Updates the app config in the store.
   *
   * @param appConfig App config.
   */
  update(appConfig: Partial<AppConfig>) {
    this.appConfigStore.update(appConfig);
  }

  /**
   * Checks if the current host matches the forced UI host and redirects if necessary.
   *
   * @param forceUiHost The host to force (e.g., "beta.square9.cloud").
   * @remarks To disable this redirect for testing/troubleshooting, set localStorage item 'disableForceUiHost' to 'true'.
   */
  private checkAndRedirectForceUiHost(forceUiHost: string): void {
    if (!forceUiHost) {
      return;
    }

    // Check if the redirect is disabled via localStorage
    const disableRedirect =
      localStorage.getItem('disableForceUiHost') === 'true';
    if (disableRedirect) {
      this.logger.info(
        `Force UI host redirect is disabled via localStorage (disableForceUiHost=true). Skipping redirect to "${forceUiHost}".`
      );
      return;
    }

    const currentHost = window.location.host;
    if (currentHost !== forceUiHost) {
      this.logger.info(
        `Current host "${currentHost}" does not match forced UI host "${forceUiHost}". Redirecting...`
      );

      // Build the new URL with the forced host, preserving the current path, search, and hash
      const newUrl = `${window.location.protocol}//${forceUiHost}${window.location.pathname}${window.location.search}${window.location.hash}`;

      // Redirect to the new URL
      window.location.replace(newUrl);
    }
  }
}
